import { getInitialChatName } from "@/lib/chat-name";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDownLeftAndArrowUpRightToCenter,
  faArrowRightToLine,
  faArrowUpRightAndArrowDownLeftFromCenter,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useChatsData } from "../../data/use-chats-data";
import { useGroupUuid } from "../../hooks/use-group-uuid";
import { useKitUuid } from "../../hooks/use-kit-uuid";
import { EditableText } from "../EditableText";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { AssistChatHistoryDropdown } from "./AssistChatHistory";
import { AssistChatMenu } from "./AssistChatMenu";
import { useAssistContext } from "./AssistContext";

type AssistBodyHeaderProps = {
  onMinimize?: () => void;
};

export const AssistBodyHeader: React.FC<AssistBodyHeaderProps> = ({
  onMinimize,
}) => {
  const { activeChat, isExpanded, setIsExpanded, isExpandable, forwardChat } =
    useAssistContext();
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { updateChat } = useChatsData(groupUuid, kitUuid);

  const handleUpdateName = (newName: string) => {
    if (!activeChat) return;
    updateChat({ uuid: activeChat.uuid, name: newName });
  };

  const handleMinimize = () => {
    setIsExpanded(false);
    if (onMinimize) {
      onMinimize();
    }
  };

  return (
    <div
      className={cn(
        "relative flex w-full shrink-0 flex-row items-center justify-between gap-2 p-4",
      )}
    >
      <div className="flex flex-1 items-center gap-2 overflow-hidden">
        {activeChat && (
          <EditableText
            value={
              activeChat.name !== "" ? activeChat.name : getInitialChatName()
            }
            onUpdate={handleUpdateName}
            disabled={!activeChat.name}
            className={cn("mr-1 truncate", {
              "w-auto": !isExpanded,
            })}
          />
        )}
        {!activeChat && !forwardChat && <div>New Chat</div>}
        {!activeChat && forwardChat && <div>{`FWD: ${forwardChat.name}`}</div>}
        {!isExpanded && <AssistChatHistoryDropdown />}
      </div>
      <div className="flex flex-row items-center gap-1">
        <AssistChatMenu />
        <Separator orientation="vertical" className="mx-2 h-6" />

        {onMinimize && (
          <Tooltip>
            <TooltipContent>Minimize chat</TooltipContent>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleMinimize}
                aria-label="Minimize chat"
                title="Minimize chat"
              >
                <FontAwesomeIcon icon={faArrowRightToLine} size="sm" />
              </Button>
            </TooltipTrigger>
          </Tooltip>
        )}

        {isExpandable && (
          <Tooltip>
            <TooltipContent>
              {isExpanded ? "Collapse chat" : "Expand chat"}
            </TooltipContent>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsExpanded(!isExpanded)}
                aria-label={isExpanded ? "Collapse chat" : "Expand chat"}
                title={isExpanded ? "Collapse chat" : "Expand chat"}
              >
                <FontAwesomeIcon
                  icon={
                    isExpanded
                      ? faArrowDownLeftAndArrowUpRightToCenter
                      : faArrowUpRightAndArrowDownLeftFromCenter
                  }
                  size="sm"
                />
              </Button>
            </TooltipTrigger>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
